import { LitElement, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { appState, type AppStateData } from '../store/AppState';
import { api, type Game, type Player, type PitchCount, type GameSubmission, type PitchLimitStatus, type InningWithPitches, type CounterPresenceEntry } from '../services/api';
import { offlineService } from '../services/offline';
import { locationService } from '../services/location';
import { toast } from './shared/Toast';
import QRCode from 'qrcode';

import './GameForm';
import './PlayerSelector';
import './GameSubmissionFlow';
import './PitchLimitIndicator';
import './InningTracker';
import './GameReviewQueue';
import './PitcherAvailabilityList';

@customElement('pitch-counter')
export class PitchCounter extends LitElement {
  createRenderRoot() {
    return this;
  }

  @state()
  private countView: AppStateData['countView'] = 'select-game';

  @state()
  private currentGame: Game | null = null;

  @state()
  private players: Player[] = [];

  @state()
  private pitches: Map<string, PitchCount> = new Map();

  @state()
  private selectedPlayerId: string | null = null;

  @state()
  private animatingPlayer: string | null = null;

  @state()
  private animationType: 'incrementing' | 'decrementing' | null = null;

  @state()
  private games: Game[] = [];

  @state()
  private loading = false;

  @state()
  private showSubmission = false;

  @state()
  private gameSubmissions: Map<string, GameSubmission> = new Map();

  @state()
  private pitchStatus: Map<string, PitchLimitStatus> = new Map();

  @state()
  private observerLink: string | null = null;

  @state()
  private generatingLink = false;

  @state()
  private linkCopied = false;

  @state()
  private qrCodeDataUrl: string | null = null;

  @state()
  private counterQrCodeDataUrl: string | null = null;

  @state()
  private shareDropdownOpen = false;

  @state()
  private shareClosing = false;

  @state()
  private contextMenuOpen = false;

  @state()
  private showDeleteConfirm = false;

  @state()
  private showEditGame = false;

  @state()
  private shareTab: 'counter' | 'observer' = 'counter';

  @state()
  private currentInningId: string | null = null;

  @state()
  private currentInningHalf: 'top' | 'bottom' | null = null;

  @state()
  private inningsData: InningWithPitches[] = [];

  @state()
  private editingJerseyForPlayer: string | null = null;

  @state()
  private jerseyEditValue: string = '';

  @state()
  private jerseySaving: boolean = false;

  // Counter-mode presence (prevent two coaches double-counting the same game)
  @state()
  private viewOnly: boolean = false;

  @state()
  private otherCounters: CounterPresenceEntry[] = [];

  @state()
  private showTakeoverModal: boolean = false;

  private clientId: string = '';
  private heartbeatInterval: number | null = null;
  private heartbeatGameId: string | null = null;
  // Once the user dismisses the takeover modal (chooses Observer mode), don't
  // re-prompt for the rest of this session — even if more counters appear.
  private takeoverPromptDismissed: boolean = false;

  private boundPopstateHandler: ((e: PopStateEvent) => void) | null = null;
  private boundInningChangedHandler: ((e: Event) => void) | null = null;
  private boundBeforeUnloadHandler: (() => void) | null = null;

  // LocalStorage key for tracking shared game access
  private static readonly SHARED_GAMES_KEY = 'pitch-counter-shared-games';

  // Get shared game IDs from localStorage
  private getSharedGameIds(): string[] {
    try {
      const stored = localStorage.getItem(PitchCounter.SHARED_GAMES_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Clean up old entries (keep last 20)
        return Array.isArray(parsed) ? parsed.slice(-20) : [];
      }
    } catch {
      // Ignore parse errors
    }
    return [];
  }

  // Save a game ID to shared games list
  private saveSharedGameId(gameId: string): void {
    try {
      const existing = this.getSharedGameIds();
      // Remove if already exists (to move to end)
      const filtered = existing.filter(id => id !== gameId);
      filtered.push(gameId);
      // Keep only last 20
      const toSave = filtered.slice(-20);
      localStorage.setItem(PitchCounter.SHARED_GAMES_KEY, JSON.stringify(toSave));
    } catch {
      // Ignore storage errors
    }
  }

  connectedCallback() {
    super.connectedCallback();

    // Generate a stable per-tab client id for counter-presence tracking.
    if (!this.clientId) {
      this.clientId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
          ? crypto.randomUUID()
          : 'client-' + Math.random().toString(36).slice(2) + '-' + Date.now();
    }

    appState.subscribe((state: AppStateData) => {
      const previousGameId = this.currentGame?.id || null;
      this.countView = state.countView;
      this.currentGame = state.currentGame;
      this.players = state.currentPlayers;
      this.pitches = state.currentPitches;
      this.selectedPlayerId = state.selectedPlayerId;

      const nextGameId = this.currentGame?.id || null;
      if (nextGameId !== previousGameId) {
        // Game changed — tear down any existing heartbeat first.
        this.stopHeartbeat();
        // Reset session-scoped flags for the new game.
        this.viewOnly = false;
        this.otherCounters = [];
        this.showTakeoverModal = false;
        this.takeoverPromptDismissed = false;
        if (this.countView === 'counting' && nextGameId) {
          this.startHeartbeat(nextGameId);
        }
      } else if (this.countView === 'counting' && nextGameId && this.heartbeatInterval == null && !this.viewOnly) {
        // Same game but heartbeat not running (e.g. after reconnect).
        this.startHeartbeat(nextGameId);
      } else if (this.countView !== 'counting') {
        this.stopHeartbeat();
      }
    });

    // Handle browser back/forward navigation
    this.boundPopstateHandler = this.handlePopstate.bind(this);
    window.addEventListener('popstate', this.boundPopstateHandler);

    // Best-effort cleanup on tab close.
    this.boundBeforeUnloadHandler = () => {
      this.sendLeaveBeacon();
    };
    window.addEventListener('beforeunload', this.boundBeforeUnloadHandler);

    // Track current inning for pitch recording
    this.boundInningChangedHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      this.currentInningId = detail?.id || null;
      this.currentInningHalf = detail?.half || null;

      // Auto-deselect player if they're on the batting team (wrong team for pitching)
      if (this.selectedPlayerId && this.currentInningHalf) {
        const pitchingTeamId = this.getPitchingTeamId();
        if (pitchingTeamId) {
          const selectedPlayer = this.players.find((p) => p.id === this.selectedPlayerId);
          if (selectedPlayer && selectedPlayer.team_id && selectedPlayer.team_id !== pitchingTeamId && !selectedPlayer.is_adhoc && !selectedPlayer.game_id) {
            this.selectedPlayerId = null;
          }
        }
      }
    };
    this.addEventListener('inning-changed', this.boundInningChangedHandler);

    this.loadGames();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.stopHeartbeat({ leave: true });
    if (this.boundPopstateHandler) {
      window.removeEventListener('popstate', this.boundPopstateHandler);
    }
    if (this.boundBeforeUnloadHandler) {
      window.removeEventListener('beforeunload', this.boundBeforeUnloadHandler);
    }
    if (this.boundInningChangedHandler) {
      this.removeEventListener('inning-changed', this.boundInningChangedHandler);
    }
  }

  // --- Counter presence ---------------------------------------------------

  private startHeartbeat(gameId: string) {
    if (this.heartbeatInterval != null) return;
    this.heartbeatGameId = gameId;
    void this.heartbeat();
    this.heartbeatInterval = window.setInterval(() => {
      void this.heartbeat();
    }, 20_000);
  }

  private stopHeartbeat(opts: { leave?: boolean } = {}) {
    if (this.heartbeatInterval != null) {
      window.clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    if (opts.leave && this.heartbeatGameId && this.clientId) {
      // Fire-and-forget; ignore errors (page may already be unloading).
      api.counterLeave(this.heartbeatGameId, this.clientId).catch(() => {});
    }
    this.heartbeatGameId = null;
  }

  private async heartbeat() {
    const gameId = this.heartbeatGameId;
    if (!gameId || !this.clientId) return;
    try {
      const resp = await api.counterHeartbeat(gameId, this.clientId);
      if (resp.evicted) {
        this.viewOnly = true;
        this.otherCounters = [];
        this.showTakeoverModal = false;
        this.stopHeartbeat();
        toast.warning("Another counter took over. You're now in Observer mode.", 6000);
        return;
      }
      this.otherCounters = resp.others || [];
      if (this.otherCounters.length > 0 && !this.viewOnly && !this.takeoverPromptDismissed) {
        this.showTakeoverModal = true;
      }
    } catch (err) {
      // Heartbeat failures are non-fatal; the user's pitch counting can
      // continue. Log for diagnostics.
      console.warn('counter heartbeat failed', err);
    }
  }

  private sendLeaveBeacon() {
    if (!this.heartbeatGameId || !this.clientId) return;
    // Best-effort fire on unload. Use sendBeacon if possible; the API
    // expects a DELETE so fall back to fetch with keepalive.
    try {
      const url =
        ((window as any).__API_URL__ || '') +
        `/api/games/${this.heartbeatGameId}/counter-heartbeat?client_id=${encodeURIComponent(this.clientId)}`;
      void fetch(url, { method: 'DELETE', credentials: 'include', keepalive: true }).catch(() => {});
    } catch {
      // Ignore — best effort.
    }
  }

  private dismissTakeoverWatchOnly() {
    this.takeoverPromptDismissed = true;
    this.showTakeoverModal = false;
    this.viewOnly = true;
    this.otherCounters = [];
    this.stopHeartbeat({ leave: true });
  }

  private async takeoverCounter() {
    if (!this.heartbeatGameId || !this.clientId) {
      this.showTakeoverModal = false;
      return;
    }
    try {
      await api.counterTakeover(this.heartbeatGameId, this.clientId);
      this.takeoverPromptDismissed = true;
      this.showTakeoverModal = false;
      this.otherCounters = [];
      toast.success("You're now counting");
    } catch (err) {
      console.error('counter takeover failed', err);
      toast.error(err instanceof Error ? err.message : 'Failed to take over counter');
    }
  }

  // Triggered from the Observer banner: take over Counter mode again after
  // the user previously chose Observer (or was evicted by another counter).
  // Backend SREMs our client_id from the eviction set so the heartbeat
  // restart can re-register us cleanly.
  private async takeoverFromBanner() {
    const gameId = this.heartbeatGameId || this.currentGame?.id || null;
    if (!gameId || !this.clientId) return;
    try {
      await api.counterTakeover(gameId, this.clientId);
      this.takeoverPromptDismissed = true;
      this.viewOnly = false;
      this.otherCounters = [];
      this.showTakeoverModal = false;
      this.startHeartbeat(gameId);
      toast.success("You're now counting");
    } catch (err) {
      console.error('observer-banner takeover failed', err);
      toast.error(err instanceof Error ? err.message : 'Failed to take over counter');
    }
  }

  private handlePopstate(e: PopStateEvent) {
    const gameId = e.state?.gameId || appState.getGameIdFromUrl();
    if (gameId) {
      // Find and load the game from cache or fetch
      const cachedGame = this.games.find((g) => g.id === gameId);
      if (cachedGame) {
        this.selectGameFromUrl(cachedGame);
      } else {
        // Game not in cache, try to fetch it
        this.loadGameFromUrl(gameId);
      }
    } else {
      // No game in URL, clear current game without updating URL
      appState.setCurrentGame(null, { updateUrl: false });
    }
  }

  private async selectGameFromUrl(game: Game) {
    // Select game but don't push history (we're responding to navigation)
    this.loading = true;
    this.observerLink = null;
    this.qrCodeDataUrl = null;
    this.counterQrCodeDataUrl = null;
    try {
      appState.setCurrentGame(game, { updateUrl: false });

      const players = await api.getPlayers(undefined, game.id);
      appState.setCurrentPlayers(players || []);

      const pitches = await api.getGamePitches(game.id);
      appState.setCurrentPitches(pitches || []);

      await this.loadAllPitchStatus();
    } catch (error) {
      console.error('Failed to load game data:', error);
    } finally {
      this.loading = false;
    }
  }

  private async loadGameFromUrl(gameId: string) {
    this.loading = true;
    try {
      // Fetch game details from API
      const game = await api.getGame(gameId);
      if (game) {
        // Save to localStorage for future access (shared game tracking)
        this.saveSharedGameId(gameId);
        
        await this.selectGameFromUrl(game);
        // Add to games cache
        if (!this.games.find((g) => g.id === game.id)) {
          this.games = [game, ...this.games];
        }
      } else {
        // Game not found, clear URL
        appState.clearGameFromUrl();
      }
    } catch (error) {
      console.error('Failed to load game from URL:', error);
      appState.clearGameFromUrl();
    } finally {
      this.loading = false;
    }
  }

  private async loadGames() {
    try {
      const allGames = await api.getGames();
      let filteredGames = allGames || [];

      // Filter games based on user role
      // Admins see all games, coaches only see games for their teams
      if (!appState.isAdmin) {
        const userTeamIds = appState.userTeamIds;
        filteredGames = filteredGames.filter(game =>
          userTeamIds.includes(game.home_team_id || '') ||
          userTeamIds.includes(game.away_team_id || '')
        );
      }

      // Merge in any shared games from localStorage
      const sharedGameIds = this.getSharedGameIds();
      const existingIds = new Set(filteredGames.map(g => g.id));
      
      // Fetch shared games that aren't already in the list
      const sharedGamesToFetch = sharedGameIds.filter(id => !existingIds.has(id));
      if (sharedGamesToFetch.length > 0) {
        const sharedGames = await Promise.all(
          sharedGamesToFetch.map(async (id) => {
            try {
              return await api.getGame(id);
            } catch {
              return null;
            }
          })
        );
        // Add valid shared games to the list
        const validSharedGames = sharedGames.filter((g): g is Game => g !== null);
        filteredGames = [...filteredGames, ...validSharedGames];
      }

      // Sort by date descending
      filteredGames.sort((a, b) => 
        new Date(b.game_date).getTime() - new Date(a.game_date).getTime()
      );

      this.games = filteredGames;
      appState.setGames(this.games);

      // Load submission status for recent games (top 5)
      const recentGames = this.games.slice(0, 5);
      await Promise.all(
        recentGames.map(async (game) => {
          try {
            const submissions = await api.getGameSubmissions(game.id);
            if (submissions && submissions.length > 0) {
              // Get the most recent submission
              this.gameSubmissions.set(game.id, submissions[0]);
            }
          } catch {
            // Ignore errors for individual game submissions
          }
        })
      );
      // Trigger re-render
      this.gameSubmissions = new Map(this.gameSubmissions);

      // Check URL for game ID to restore on refresh (shared link support)
      const urlGameId = appState.getGameIdFromUrl();
      if (urlGameId && !this.currentGame) {
        const cachedGame = this.games.find((g) => g.id === urlGameId);
        if (cachedGame) {
          // Load game from URL - selectGameFromUrl handles everything
          await this.selectGameFromUrl(cachedGame);
        } else {
          // Game not in list (could be from shared link), try fetching it directly
          await this.loadGameFromUrl(urlGameId);
        }
      }
    } catch (error) {
      console.error('Failed to load games:', error);
    }
  }

  private async selectGame(game: Game) {
    this.loading = true;
    this.observerLink = null; // Reset links when switching games
    this.qrCodeDataUrl = null;
    this.counterQrCodeDataUrl = null;
    try {
      appState.setCurrentGame(game);

      // Load players for this game's teams
      const players = await api.getPlayers(undefined, game.id);
      appState.setCurrentPlayers(players || []);

      // Load existing pitch counts
      const pitches = await api.getGamePitches(game.id);
      appState.setCurrentPitches(pitches || []);

      // Load pitch status for all players
      await this.loadAllPitchStatus();

      // Load innings data for per-inning display
      await this.loadInningsData(game.id);
    } catch (error) {
      console.error('Failed to load game data:', error);
    } finally {
      this.loading = false;
    }
  }

  private async loadInningsData(gameId: string) {
    try {
      this.inningsData = (await api.getGameInnings(gameId)) || [];
    } catch {
      this.inningsData = [];
    }
  }

  private async handleGameCreated(e: CustomEvent<Game>) {
    await this.selectGame(e.detail);
    this.games = [e.detail, ...this.games];
  }

  private handlePlayerSelected(e: CustomEvent<string>) {
    appState.selectPlayer(e.detail);
    this.loadPitchStatus(e.detail);
  }

  private async loadPitchStatus(playerId: string) {
    if (!this.currentGame) return;
    try {
      const status = await api.getPlayerPitchStatus(playerId, this.currentGame.id);
      this.pitchStatus.set(playerId, status);
      this.pitchStatus = new Map(this.pitchStatus); // Trigger update
    } catch (err) {
      console.error('Failed to load pitch status:', err);
    }
  }

  private async loadAllPitchStatus() {
    if (!this.currentGame || this.players.length === 0) return;
    try {
      const statuses = await api.getGamePitchStatus(this.currentGame.id);
      this.pitchStatus = new Map((statuses || []).map((s) => [s.player_id, s]));
    } catch (err) {
      console.error('Failed to load pitch statuses:', err);
    }
  }

  private async handlePlayerAdded(e: CustomEvent<Player>) {
    this.players = [...this.players, e.detail];
    appState.setCurrentPlayers(this.players);
    appState.selectPlayer(e.detail.id);
  }

  private getSelectedPlayerCount(): number {
    if (!this.selectedPlayerId) return 0;
    return this.pitches.get(this.selectedPlayerId)?.count ?? 0;
  }

  private getSelectedPlayer(): Player | undefined {
    return this.players.find((p) => p.id === this.selectedPlayerId);
  }

  /** Whether the current user can edit jersey numbers for the given player. */
  private canEditPlayerJersey(player: Player | undefined): boolean {
    if (!player || !player.team_id) return false;
    if (appState.isAdmin) return true;
    return appState.userTeamIds.includes(player.team_id);
  }

  private startEditJersey(player: Player) {
    this.editingJerseyForPlayer = player.id;
    this.jerseyEditValue = player.jersey_number != null ? String(player.jersey_number) : '';
  }

  private cancelEditJersey() {
    this.editingJerseyForPlayer = null;
    this.jerseyEditValue = '';
  }

  private async saveJersey(player: Player) {
    if (this.jerseySaving) return;
    const trimmed = this.jerseyEditValue.trim();
    let newValue: number | null = null;
    if (trimmed !== '') {
      const parsed = parseInt(trimmed, 10);
      if (!Number.isFinite(parsed) || parsed < 0 || parsed > 999) {
        toast.error('Jersey number must be 0-999');
        return;
      }
      newValue = parsed;
    }
    this.jerseySaving = true;
    try {
      const updated = await api.updatePlayerJersey(player.id, newValue);
      // Push the updated player into appState so the change survives
      // re-renders driven by appState subscriptions (otherwise selecting
      // a different pitcher / any state push wipes the local update).
      const nextPlayers = this.players.map((p) =>
        p.id === player.id ? { ...p, jersey_number: updated.jersey_number } : p
      );
      this.players = nextPlayers;
      appState.setCurrentPlayers(nextPlayers);
      this.editingJerseyForPlayer = null;
      this.jerseyEditValue = '';
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save jersey number');
    } finally {
      this.jerseySaving = false;
    }
  }

  /** Returns the team_id of the team that should be pitching in the current half-inning */
  private getPitchingTeamId(): string | null {
    if (!this.currentInningHalf || !this.currentGame) return null;
    // Top of inning: home team pitches (away team bats)
    // Bottom of inning: away team pitches (home team bats)
    if (this.currentInningHalf === 'top') {
      return this.currentGame.home_team_id || null;
    }
    return this.currentGame.away_team_id || null;
  }

  /** Returns players filtered to only the pitching team, or all if no inning context */
  private getActivePlayers(): Player[] {
    const pitchingTeamId = this.getPitchingTeamId();
    if (!pitchingTeamId) return this.players;
    return this.players.filter(
      (p) => p.team_id === pitchingTeamId || p.is_adhoc || p.game_id
    );
  }

  /** Returns the set of player IDs who have pitched in the current inning */
  private getCurrentInningPitchers(): Set<string> {
    if (!this.currentInningId) return new Set();
    const currentInning = this.inningsData.find((i) => i.id === this.currentInningId);
    if (!currentInning?.pitches) return new Set();
    return new Set(currentInning.pitches.filter((p) => p.pitch_count > 0).map((p) => p.player_id));
  }

  private getTotalPitchCount(): number {
    let total = 0;
    for (const pitch of this.pitches.values()) {
      total += pitch.count;
    }
    return total;
  }

  private async incrementPitch() {
    if (!this.selectedPlayerId || !this.currentGame) return;

    const status = this.pitchStatus.get(this.selectedPlayerId);
    const selectedPlayer = this.getSelectedPlayer();

    // Check if limit reached
    if (status?.status === 'limit_reached') {
      const confirmed = confirm(
        `${selectedPlayer?.name || 'This player'} has reached their pitch limit (${status.max_pitches}/${status.max_pitches}).\n\nContinue anyway? This may violate Baseball Manitoba rules.`
      );
      if (!confirmed) return;
    }

    const currentCount = this.getSelectedPlayerCount();
    const newCount = currentCount + 1;

    // Animate
    this.animatingPlayer = this.selectedPlayerId;
    this.animationType = 'incrementing';
    setTimeout(() => {
      this.animatingPlayer = null;
      this.animationType = null;
    }, 150);

    // Trigger haptic feedback at warning threshold
    if (status?.status === 'warning' && 'vibrate' in navigator) {
      navigator.vibrate(100);
    }

    // Update local state immediately
    appState.updatePitchCount(this.selectedPlayerId, newCount);

    // Also update local pitchStatus for optimistic UI update of progress bar
    if (status) {
      const updatedStatus = {
        ...status,
        current_count: newCount,
        remaining_pitches: Math.max(0, status.max_pitches - newCount),
        percentage: Math.round((newCount / status.max_pitches) * 100),
        status: newCount >= status.max_pitches ? 'limit_reached' as const :
                newCount >= status.warning_threshold ? 'warning' as const : 'ok' as const,
      };
      this.pitchStatus = new Map(this.pitchStatus).set(this.selectedPlayerId, updatedStatus);
    }

    // Show warning toast when a rest threshold is crossed
    if (status?.rest_thresholds) {
      for (const t of status.rest_thresholds) {
        if (currentCount < t.pitch_count && newCount >= t.pitch_count) {
          const playerName = this.getSelectedPlayer()?.name || 'Player';
          toast.warning(
            `${playerName} will need ${t.rest_days} day${t.rest_days !== 1 ? 's' : ''} rest before next game`
          );
          break;
        }
      }
    }

    // Sync with server (or queue for later)
    try {
      const result = await api.addPitchEvent(this.currentGame.id, {
        player_id: this.selectedPlayerId,
        inning_id: this.currentInningId || undefined,
      });
      appState.updatePitchCount(this.selectedPlayerId, result.count, result.id);

      // Reload pitch status to get updated limits
      await this.loadPitchStatus(this.selectedPlayerId);

      // Refresh innings data for per-inning display (non-blocking)
      this.loadInningsData(this.currentGame.id);
    } catch (error) {
      // Queue for offline sync
      await offlineService.queueAction('add_pitch_event', {
        gameId: this.currentGame.id,
        playerId: this.selectedPlayerId,
        inningId: this.currentInningId || undefined,
      });
    }
  }

  private async decrementPitch() {
    if (!this.selectedPlayerId || !this.currentGame) return;

    const currentCount = this.getSelectedPlayerCount();
    if (currentCount <= 0) return;

    const newCount = currentCount - 1;
    const status = this.pitchStatus.get(this.selectedPlayerId);

    // Animate
    this.animatingPlayer = this.selectedPlayerId;
    this.animationType = 'decrementing';
    setTimeout(() => {
      this.animatingPlayer = null;
      this.animationType = null;
    }, 150);

    // Update local state immediately
    appState.updatePitchCount(this.selectedPlayerId, newCount);

    // Also update local pitchStatus for optimistic UI update of progress bar
    if (status) {
      const updatedStatus = {
        ...status,
        current_count: newCount,
        remaining_pitches: Math.max(0, status.max_pitches - newCount),
        percentage: Math.round((newCount / status.max_pitches) * 100),
        status: newCount >= status.max_pitches ? 'limit_reached' as const :
                newCount >= status.warning_threshold ? 'warning' as const : 'ok' as const,
      };
      this.pitchStatus = new Map(this.pitchStatus).set(this.selectedPlayerId, updatedStatus);
    }

    // Sync with server
    try {
      const result = await api.removeLatestPitchEvent(this.currentGame.id, this.selectedPlayerId);
      appState.updatePitchCount(this.selectedPlayerId, result.count, result.id);

      // Refresh innings data for per-inning display (non-blocking)
      this.loadInningsData(this.currentGame.id);
    } catch (error) {
      await offlineService.queueAction('remove_pitch_event', {
        gameId: this.currentGame.id,
        playerId: this.selectedPlayerId,
      });
    }
  }

  private endGame() {
    // Show submission flow instead of immediately closing
    this.showSubmission = true;
  }

  private handleGameUpdated(e: CustomEvent<Game>) {
    const updatedGame = e.detail;
    appState.setCurrentGame(updatedGame);
    this.showEditGame = false;
  }

  private async confirmDeleteGame() {
    if (!this.currentGame) return;
    try {
      await api.deleteGame(this.currentGame.id);
      this.showDeleteConfirm = false;
      this.closeGame();
    } catch (err) {
      console.error('Failed to delete game:', err);
      alert('Failed to delete game');
    }
  }

  private canDeleteGame(): boolean {
    if (!this.currentGame) return false;
    if (appState.isAdmin) return true;
    return this.currentGame.created_by === appState.user?.id;
  }

  private closeGame() {
    appState.setCurrentGame(null);
    this.showSubmission = false;
    this.currentInningId = null;
    this.currentInningHalf = null;
    this.inningsData = [];
    this.shareDropdownOpen = false;
    this.contextMenuOpen = false;
    this.observerLink = null;
    this.qrCodeDataUrl = null;
    this.counterQrCodeDataUrl = null;
    this.loadGames(); // Refresh game list
  }

  private handleGameSubmitted() {
    this.closeGame();
  }

  private async generateObserverLink() {
    if (!this.currentGame) return;
    this.generatingLink = true;
    try {
      const result = await api.generateObserverLink(this.currentGame.id);
      // Construct URL on the client side to ensure correct scheme and host
      this.observerLink = `${window.location.origin}/observe?token=${result.token}`;

      // Generate QR code
      this.qrCodeDataUrl = await QRCode.toDataURL(this.observerLink, {
        width: 200,
        margin: 2,
        color: {
          dark: '#1e3a5f', // Baseball Manitoba blue
          light: '#ffffff',
        },
      });
    } catch (error) {
      console.error('Failed to generate observer link:', error);
    } finally {
      this.generatingLink = false;
    }
  }

  private async copyObserverLink() {
    if (!this.observerLink) return;
    try {
      await navigator.clipboard.writeText(this.observerLink);
      this.linkCopied = true;
      setTimeout(() => {
        this.linkCopied = false;
      }, 1500);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  }

  private async copyGameId() {
    if (!this.currentGame?.id) return;
    try {
      await navigator.clipboard.writeText(this.currentGame.id);
      toast.success('Game ID copied');
    } catch (error) {
      console.error('Failed to copy game ID:', error);
      toast.error('Failed to copy game ID');
    }
  }

  private async shareObserverLink() {
    if (!this.observerLink || !navigator.share) return;
    try {
      await navigator.share({
        title: `Watch: ${this.currentGame?.name}`,
        text: 'View live pitch counts',
        url: this.observerLink,
      });
    } catch (error) {
      // User cancelled or share failed - ignore
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to share:', error);
      }
    }
  }

  private getCounterLink(): string {
    if (!this.currentGame) return '';
    return `${window.location.origin}/?game=${this.currentGame.id}`;
  }

  private async generateCounterQrCode() {
    const link = this.getCounterLink();
    if (!link || this.counterQrCodeDataUrl) return;
    
    try {
      this.counterQrCodeDataUrl = await QRCode.toDataURL(link, {
        width: 200,
        margin: 2,
        color: {
          dark: '#1e3a5f',
          light: '#ffffff',
        },
      });
    } catch (error) {
      console.error('Failed to generate counter QR code:', error);
    }
  }

  private closeShareDropdown() {
    if (!this.shareDropdownOpen || this.shareClosing) return;
    this.shareClosing = true;
    // Match the slide-up duration in input.css (0.25s) so the panel and
    // backdrop fade out before being removed from the DOM.
    window.setTimeout(() => {
      this.shareDropdownOpen = false;
      this.shareClosing = false;
    }, 200);
  }

  private async openShareDropdown() {
    if (this.shareDropdownOpen) {
      this.closeShareDropdown();
      return;
    }
    this.shareDropdownOpen = true;
    // Generate counter QR code when opening
    this.generateCounterQrCode();
    // Pre-generate the observer link so the Observer tab is ready
    // before the user switches to it (no manual button required).
    if (!this.observerLink && !this.generatingLink) {
      void this.generateObserverLink();
    }
  }

  private switchShareTab(tab: 'counter' | 'observer') {
    this.shareTab = tab;
    if (tab === 'observer' && !this.observerLink && !this.generatingLink) {
      void this.generateObserverLink();
    }
  }

  private async copyCounterLink() {
    const link = this.getCounterLink();
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      this.linkCopied = true;
      setTimeout(() => {
        this.linkCopied = false;
      }, 1500);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  }

  private async shareCounterLink() {
    const link = this.getCounterLink();
    if (!link || !navigator.share) return;
    try {
      await navigator.share({
        title: `Count pitches: ${this.currentGame?.name}`,
        text: 'Help track pitch counts for this game',
        url: link,
      });
    } catch (error) {
      // User cancelled or share failed - ignore
      if ((error as Error).name !== 'AbortError') {
        console.error('Failed to share:', error);
      }
    }
  }

  render() {
    if (this.loading) {
      return html`
        <div class="flex items-center justify-center h-64">
          <span class="loading loading-spinner loading-lg text-primary"></span>
        </div>
      `;
    }

    if (this.countView === 'select-game') {
      return this.renderGameSelection();
    }

    return this.renderCounting();
  }

  private renderTakeoverModal() {
    const others = this.otherCounters;
    if (others.length === 0) return nothing;
    const first = others[0]?.display_name || 'Another coach';
    const moreCount = others.length - 1;
    const subject =
      moreCount > 0
        ? `${first} and ${moreCount} other${moreCount === 1 ? '' : 's'}`
        : first;
    return html`
      <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 animate-fade-in">
        <div class="bg-base-100 rounded-2xl shadow-2xl p-6 max-w-sm w-full animate-slide-up" @click=${(e: Event) => e.stopPropagation()}>
          <h3 class="text-lg font-bold">Already in use</h3>
          <p class="mt-2 text-sm text-base-content/80">
            <strong>${subject}</strong> ${others.length > 1 || moreCount > 0 ? 'are' : 'is'} currently using Counter mode for this game.
          </p>
          <p class="mt-2 text-sm text-warning">
            Two counters at once will double the pitch count. Observer mode is recommended.
          </p>
          <div class="flex flex-col gap-2 mt-5">
            <button class="btn btn-primary min-h-[44px]" @click=${this.dismissTakeoverWatchOnly}>
              Use Observer mode
            </button>
            <button class="btn btn-ghost min-h-[44px]" @click=${this.takeoverCounter}>
              Take over
            </button>
          </div>
        </div>
      </div>
    `;
  }

  private renderSubmissionBadge(status: string) {
    const badgeConfig: Record<string, { class: string; text: string }> = {
      pending: { class: 'badge-warning', text: 'Pending' },
      pending_away: { class: 'badge-warning', text: 'Awaiting Coach' },
      pending_admin: { class: 'badge-warning', text: 'Awaiting Admin' },
      approved: { class: 'badge-success', text: 'Approved' },
      rejected: { class: 'badge-error', text: 'Rejected' },
      changes_requested: { class: 'badge-error', text: 'Changes Needed' },
    };

    const config = badgeConfig[status] || { class: 'badge-ghost', text: status };

    return html`<span class="badge badge-sm whitespace-nowrap shrink-0 ${config.class}">${config.text}</span>`;
  }

  private renderGameSelection() {
    return html`
      <div class="space-y-6 animate-fade-in">
        <h2 class="text-2xl font-bold">Start Counting</h2>

        <!-- Coach review queue -->
        ${appState.isCoach ? html`<game-review-queue></game-review-queue>` : nothing}

        <!-- Pitcher availability for coaches -->
        ${appState.userTeamIds.length > 0 ? html`
          <div class="card bg-base-100 shadow-sm" data-testid="pitcher-availability-count-card">
            <div class="card-body">
              ${appState.userTeamIds.map(teamId => html`
                <pitcher-availability-list .teamId=${teamId}></pitcher-availability-list>
              `)}
            </div>
          </div>
        ` : nothing}

        <!-- Create new game -->
        <game-form @game-created=${this.handleGameCreated}></game-form>

        <!-- Or select existing game -->
        ${this.games.length > 0 ? html`
          <div class="divider">OR continue existing game</div>

          <div class="space-y-2">
            ${this.games.slice(0, 5).map((game) => {
              const submission = this.gameSubmissions.get(game.id);
              const needsAction = submission?.status === 'changes_requested' || submission?.status === 'rejected';
              return html`
                <button
                  class="w-full card shadow-sm hover:shadow-md transition-shadow ${needsAction ? 'bg-error/10 border border-error/30' : 'bg-base-100'}"
                  @click=${() => this.selectGame(game)}
                >
                  <div class="card-body py-3 px-4">
                    <div class="flex justify-between items-center">
                      <div class="text-left min-w-0 flex-1">
                        <div class="font-semibold flex items-center gap-2">
                          <span class="truncate">${game.name}</span>
                          ${submission ? this.renderSubmissionBadge(submission.status) : nothing}
                        </div>
                        <div class="text-sm text-base-content/70">
                          ${new Date(game.game_date).toLocaleDateString()}
                          ${game.location ? ` - ${game.location}` : ''}
                        </div>
                        ${submission?.status === 'changes_requested' ? html`
                          <div class="text-xs text-error mt-1">
                            <span class="font-medium">Changes requested:</span> ${submission.change_request_message || 'No details provided'}
                          </div>
                        ` : nothing}
                      </div>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-base-content/50" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              `;
            })}
          </div>
        ` : nothing}
      </div>
    `;
  }

  private isGameLocked(): boolean {
    return this.currentGame?.status === 'approved';
  }

  /** Get the team currently at bat based on inning half */
  private getBattingTeamId(): string | null {
    if (!this.currentInningHalf || !this.currentGame) return null;
    // Top of inning: away team bats
    // Bottom of inning: home team bats
    return this.currentInningHalf === 'top'
      ? this.currentGame.away_team_id || null
      : this.currentGame.home_team_id || null;
  }

  private renderCounting() {
    // Show submission flow if ending game
    if (this.showSubmission) {
      return html`
        <div class="space-y-4 animate-fade-in">
          <div class="flex justify-between items-center">
            <h2 class="text-xl font-bold">Complete Game</h2>
            <button class="btn btn-ghost btn-sm" @click=${this.closeGame}>Skip & Close</button>
          </div>

          <game-submission-flow
            .gameId=${this.currentGame?.id || ''}
            @game-submitted=${this.handleGameSubmitted}
          ></game-submission-flow>
        </div>
      `;
    }

    const selectedPlayer = this.getSelectedPlayer();
    const count = this.getSelectedPlayerCount();
    const isAnimating = this.animatingPlayer === this.selectedPlayerId;
    const isLocked = this.isGameLocked();

    const currentPitchStatus = this.selectedPlayerId
      ? this.pitchStatus.get(this.selectedPlayerId)
      : null;

    const homeName = this.currentGame?.home_team_name || 'Home';
    const awayName = this.currentGame?.away_team_name || 'Away';
    const battingTeamId = this.getBattingTeamId();
    const isHomeBatting = battingTeamId === this.currentGame?.home_team_id;
    const isAwayBatting = battingTeamId === this.currentGame?.away_team_id;

    // Get submission status for current game
    const submission = this.currentGame ? this.gameSubmissions.get(this.currentGame.id) : null;
    const hasChangesRequested = submission?.status === 'changes_requested';

    return html`
      <div class="space-y-3 animate-fade-in">
        <!-- Observer banner (another counter is active) -->
        ${this.viewOnly ? html`
          <div class="alert alert-info text-sm py-2 flex flex-row items-center gap-2 animate-fade-in">
            <span class="text-base shrink-0">👁</span>
            <span class="flex-1"><strong>Observer</strong> — you're watching this game live.</span>
            <button
              class="btn btn-sm btn-primary min-h-[36px] shrink-0"
              @click=${this.takeoverFromBanner}
              aria-label="Take over and start counting"
            >
              Take over
            </button>
          </div>
        ` : nothing}

        <!-- Takeover prompt modal -->
        ${this.showTakeoverModal && !this.viewOnly ? this.renderTakeoverModal() : nothing}

        <!-- Locked game banner -->
        ${isLocked ? html`
          <div class="alert alert-warning text-sm py-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
            <span><strong>Locked</strong> — Pitch counts cannot be modified</span>
          </div>
        ` : nothing}

        <!-- Changes requested banner -->
        ${hasChangesRequested ? html`
          <div class="alert alert-error text-sm py-2">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <div>
              <strong>Changes requested</strong>
              <p class="text-xs mt-0.5 opacity-80">${submission!.change_request_message || 'No details provided'}</p>
            </div>
          </div>
        ` : nothing}

        <!-- Game Header Card -->
        <div class="game-header-card">
          <!-- Teams Matchup -->
          <div class="matchup-container mb-3 sm:mb-4">
            <!-- Away Team -->
            <div class="team-side ${isAwayBatting ? 'at-bat' : ''}">
              <span class="team-label">Away</span>
              <span class="team-name">${awayName}</span>
              ${isAwayBatting ? html`<span class="batting-indicator">⚾ At Bat</span>` : nothing}
            </div>

            <!-- VS Divider -->
            <div class="vs-divider">
              <span class="vs-text">@</span>
            </div>

            <!-- Home Team -->
            <div class="team-side ${isHomeBatting ? 'at-bat' : ''}">
              <span class="team-label">Home</span>
              <span class="team-name">${homeName}</span>
              ${isHomeBatting ? html`<span class="batting-indicator">⚾ At Bat</span>` : nothing}
            </div>
          </div>

          <!-- Combined meta row: location + date on the left, action buttons on the right -->
          <div class="game-meta-row">
            <div class="game-info-group">
              ${this.currentGame?.location ? html`
                <div class="game-info-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path stroke-linecap="round" stroke-linejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span class="truncate">${this.currentGame.location}</span>
                </div>
              ` : nothing}
              ${this.currentGame?.game_date ? html`
                <div class="game-info-item">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <span>${new Date(this.currentGame.game_date).toLocaleDateString()}</span>
                </div>
              ` : nothing}
            </div>

            <div class="flex items-center gap-2 shrink-0">
              <button
                class="btn btn-primary btn-xs gap-1.5"
                @click=${this.openShareDropdown}
              >
                <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
                <span class="text-xs font-medium">Share</span>
              </button>
              <div class="game-actions-group">
              <!-- Three-dots context menu: edit / copy id / delete. Always
                   rendered so locked games still have a way to copy the
                   game id; Edit/Delete items are conditional on !isLocked. -->
              <div class="dropdown dropdown-bottom dropdown-end">
                <button
                  tabindex="0"
                  class="btn btn-ghost btn-xs btn-square"
                  @click=${() => { this.contextMenuOpen = !this.contextMenuOpen; }}
                  title="More options"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
                  </svg>
                </button>
                <ul tabindex="0" class="dropdown-content z-50 menu menu-sm p-2 shadow-lg bg-base-100 rounded-box w-44">
                  ${!isLocked ? html`
                    <li>
                      <button class="w-full text-left flex items-center gap-2" @click=${() => { this.showEditGame = true; this.contextMenuOpen = false; }}>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Edit game
                      </button>
                    </li>
                  ` : nothing}
                  <li>
                    <button class="w-full text-left flex items-center gap-2" @click=${() => { void this.copyGameId(); this.contextMenuOpen = false; }}>
                      <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      Copy game ID
                    </button>
                  </li>
                  ${!isLocked && this.canDeleteGame() ? html`
                    <li>
                      <button class="w-full text-left flex items-center gap-2 text-error" @click=${() => { this.showDeleteConfirm = true; this.contextMenuOpen = false; }}>
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                          <path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Delete game
                      </button>
                    </li>
                  ` : nothing}
                </ul>
              </div>
              ${isLocked ? html`
                <button class="btn btn-ghost btn-xs gap-1" @click=${this.closeGame}>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span class="hidden sm:inline text-xs">Close</span>
                </button>
              ` : nothing}
              </div>
            </div>
          </div>
        </div>

        <!-- Inning tracker -->
        ${this.currentGame
          ? html`
              <inning-tracker
                .gameId=${this.currentGame.id}
                .homeTeamName=${homeName}
                .awayTeamName=${awayName}
                .viewOnly=${this.viewOnly}
                compact
              ></inning-tracker>
            `
          : nothing}

        <!-- Share bottom sheet -->
        ${this.shareDropdownOpen ? html`
          <div
            class="fixed inset-0 bg-black/50 z-50 transition-opacity duration-200 ease-out ${this.shareClosing ? 'opacity-0' : 'animate-fade-in'}"
            @click=${this.closeShareDropdown}
          ></div>
          <div class="fixed inset-x-0 bottom-0 z-50 sm:inset-auto sm:top-1/2 sm:left-1/2 sm:-translate-x-1/2 sm:-translate-y-1/2 sm:w-full sm:max-w-md">
            <div class="bg-base-100 rounded-t-3xl sm:rounded-2xl shadow-2xl overflow-hidden transition-all duration-200 ease-out ${this.shareClosing ? 'opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95' : 'animate-slide-up'}">
              <!-- Drag handle (mobile) -->
              <div class="flex justify-center pt-3 pb-1 sm:hidden">
                <div class="w-10 h-1 bg-base-300 rounded-full"></div>
              </div>

              <!-- Header -->
              <div class="flex justify-between items-center px-5 py-3 border-b border-base-200">
                <h3 class="text-lg font-semibold">Share Game</h3>
                <button
                  class="btn btn-ghost btn-sm btn-circle min-h-[44px] min-w-[44px]"
                  aria-label="Close share modal"
                  @click=${this.closeShareDropdown}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <!-- Tabs (animated pill indicator slides between Counter/Observer) -->
              <div
                class="relative grid grid-cols-2 gap-2 p-4 bg-base-200/50"
                role="tablist"
                aria-label="Share game options"
              >
                <!-- Sliding pill indicator -->
                <div
                  class="absolute top-4 bottom-4 left-4 rounded-xl bg-primary shadow-lg transition-transform duration-200 ease-out pointer-events-none"
                  style=${`width: calc(50% - 1.25rem); transform: translateX(${this.shareTab === 'counter' ? '0' : 'calc(100% + 0.5rem)'});`}
                  aria-hidden="true"
                ></div>
                <button
                  role="tab"
                  aria-selected=${this.shareTab === 'counter'}
                  class="relative z-10 flex flex-col items-center gap-2 p-4 rounded-xl transition-colors duration-200 ease-out min-h-[44px] ${this.shareTab === 'counter'
                    ? 'text-primary-content'
                    : 'text-base-content hover:bg-base-100'}"
                  @click=${() => this.switchShareTab('counter')}
                >
                  <div class="w-10 h-10 rounded-full ${this.shareTab === 'counter' ? 'bg-primary-content/20' : 'bg-primary/10'} flex items-center justify-center transition-colors duration-200 ease-out">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ${this.shareTab === 'counter' ? '' : 'text-primary'}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
                    </svg>
                  </div>
                  <span class="font-medium text-sm">Counter</span>
                  <span class="text-xs ${this.shareTab === 'counter' ? 'text-primary-content/70' : 'text-base-content/50'} transition-colors duration-200 ease-out">Adds pitches</span>
                </button>
                <button
                  role="tab"
                  aria-selected=${this.shareTab === 'observer'}
                  class="relative z-10 flex flex-col items-center gap-2 p-4 rounded-xl transition-colors duration-200 ease-out min-h-[44px] ${this.shareTab === 'observer'
                    ? 'text-primary-content'
                    : 'text-base-content hover:bg-base-100'}"
                  @click=${() => this.switchShareTab('observer')}
                >
                  <div class="w-10 h-10 rounded-full ${this.shareTab === 'observer' ? 'bg-primary-content/20' : 'bg-primary/10'} flex items-center justify-center transition-colors duration-200 ease-out">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 ${this.shareTab === 'observer' ? '' : 'text-primary'}" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  </div>
                  <span class="font-medium text-sm">Observer</span>
                  <span class="text-xs ${this.shareTab === 'observer' ? 'text-primary-content/70' : 'text-base-content/50'} transition-colors duration-200 ease-out">Watches counts live</span>
                </button>
              </div>

              <!-- Mode guidance: prevents two-counter double-counting (client feedback #2) -->
              <div class="px-4 pb-2 -mt-2">
                <p class="text-xs text-base-content/60 leading-snug">
                  ${this.shareTab === 'counter'
                    ? html`<span class="font-medium text-warning">Only one person should use Counter per game.</span> Two counters at the same time will double the pitch count. Share <span class="font-medium">Observer</span> with the other team instead.`
                    : html`Send to the visiting team or scorers so they can watch pitch counts live without affecting them. No sign-in required.`}
                </p>
              </div>

              <!-- Content (cross-fade between tabs; fixed min-height avoids jump) -->
              <div class="p-5 max-h-[60vh] overflow-y-auto">
                <div class="relative min-h-[360px]">
                  <!-- Counter Tab Content -->
                  <div
                    role="tabpanel"
                    aria-hidden=${this.shareTab !== 'counter'}
                    class="transition-opacity duration-200 ease-out ${this.shareTab === 'counter' ? 'opacity-100' : 'opacity-0 pointer-events-none absolute inset-0'}"
                  >
                    <div class="space-y-4">
                      <!-- QR Code -->
                      <div class="flex flex-col items-center">
                        ${this.counterQrCodeDataUrl ? html`
                          <div class="bg-white p-3 rounded-2xl shadow-sm animate-fade-in">
                            <img
                              src=${this.counterQrCodeDataUrl}
                              alt="QR Code"
                              class="w-40 h-40 sm:w-48 sm:h-48"
                            />
                          </div>
                          <p class="text-xs text-base-content/60 mt-2">Scan to count pitches</p>
                          <p class="text-xs text-warning mt-1">Requires sign-in</p>
                        ` : html`
                          <div class="w-40 h-40 flex items-center justify-center bg-base-200 rounded-2xl">
                            <span class="loading loading-spinner loading-md"></span>
                          </div>
                        `}
                      </div>

                      <div class="divider text-xs text-base-content/40">or share link</div>

                      <!-- Link & Actions -->
                      <div class="flex gap-2">
                        <input
                          class="input input-bordered flex-1 font-mono text-xs pr-2 min-h-[44px]"
                          readonly
                          .value=${this.getCounterLink()}
                          @click=${(e: Event) => (e.target as HTMLInputElement).select()}
                        />
                        <button
                          class="btn shrink-0 min-h-[44px] min-w-[44px] gap-1 transition-all duration-200 ease-out ${this.linkCopied && this.shareTab === 'counter' ? 'btn-success scale-105' : 'btn-ghost'}"
                          aria-label=${this.linkCopied && this.shareTab === 'counter' ? 'Copied' : 'Copy link'}
                          @click=${this.copyCounterLink}
                        >
                          ${this.linkCopied && this.shareTab === 'counter' ? html`
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span class="text-xs font-medium">Copied!</span>
                          ` : html`
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          `}
                        </button>
                      </div>

                      ${'share' in navigator ? html`
                        <button class="btn btn-primary btn-block gap-2 min-h-[44px]" @click=${this.shareCounterLink}>
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          Share Counter Link
                        </button>
                      ` : nothing}
                    </div>
                  </div>

                  <!-- Observer Tab Content (auto-generated link; no manual button) -->
                  <div
                    role="tabpanel"
                    aria-hidden=${this.shareTab !== 'observer'}
                    class="transition-opacity duration-200 ease-out ${this.shareTab === 'observer' ? 'opacity-100' : 'opacity-0 pointer-events-none absolute inset-0'}"
                  >
                    <div class="space-y-4">
                      <!-- QR Code (or spinner while generating) -->
                      <div class="flex flex-col items-center">
                        ${this.observerLink && this.qrCodeDataUrl ? html`
                          <div class="bg-white p-3 rounded-2xl shadow-sm animate-fade-in">
                            <img
                              src=${this.qrCodeDataUrl}
                              alt="QR Code"
                              class="w-40 h-40 sm:w-48 sm:h-48"
                            />
                          </div>
                          <p class="text-xs text-base-content/60 mt-2">Scan to view game</p>
                          <p class="text-xs text-base-content/40 mt-1">No sign-in required • Expires in 24h</p>
                        ` : html`
                          <div
                            class="w-40 h-40 flex flex-col items-center justify-center gap-2 bg-base-200 rounded-2xl"
                            role="status"
                            aria-label="Generating observer link"
                          >
                            <span class="loading loading-spinner loading-md"></span>
                            <span class="text-xs text-base-content/50">Generating link…</span>
                          </div>
                        `}
                      </div>

                      <div class="divider text-xs text-base-content/40">or share link</div>

                      <!-- Link & Actions -->
                      <div class="flex gap-2">
                        <input
                          class="input input-bordered flex-1 font-mono text-xs pr-2 min-h-[44px]"
                          readonly
                          placeholder=${this.observerLink ? '' : 'Generating…'}
                          .value=${this.observerLink || ''}
                          @click=${(e: Event) => (e.target as HTMLInputElement).select()}
                          ?disabled=${!this.observerLink}
                        />
                        <button
                          class="btn shrink-0 min-h-[44px] min-w-[44px] gap-1 transition-all duration-200 ease-out ${this.linkCopied && this.shareTab === 'observer' ? 'btn-success scale-105' : 'btn-ghost'}"
                          aria-label=${this.linkCopied && this.shareTab === 'observer' ? 'Copied' : 'Copy link'}
                          @click=${this.copyObserverLink}
                          ?disabled=${!this.observerLink}
                        >
                          ${this.linkCopied && this.shareTab === 'observer' ? html`
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                            </svg>
                            <span class="text-xs font-medium">Copied!</span>
                          ` : html`
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          `}
                        </button>
                      </div>

                      ${'share' in navigator ? html`
                        <button
                          class="btn btn-primary btn-block gap-2 min-h-[44px]"
                          @click=${this.shareObserverLink}
                          ?disabled=${!this.observerLink}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                          Share Observer Link
                        </button>
                      ` : nothing}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ` : nothing}

        <!-- Edit game modal -->
        ${this.showEditGame ? html`
          <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click=${() => { this.showEditGame = false; }}>
            <div class="bg-base-100 rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" @click=${(e: Event) => e.stopPropagation()}>
              <game-form
                editMode
                .editGame=${this.currentGame}
                @game-updated=${this.handleGameUpdated}
                @edit-cancelled=${() => { this.showEditGame = false; }}
              ></game-form>
            </div>
          </div>
        ` : nothing}

        <!-- Delete confirmation modal -->
        ${this.showDeleteConfirm ? html`
          <div class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" @click=${() => { this.showDeleteConfirm = false; }}>
            <div class="bg-base-100 rounded-2xl shadow-2xl p-6 max-w-sm w-full" @click=${(e: Event) => e.stopPropagation()}>
              <h3 class="text-lg font-bold text-error">Delete Game?</h3>
              <p class="mt-2 text-sm text-base-content/70">
                This will permanently delete this game and all pitch data. This cannot be undone.
              </p>
              <div class="flex gap-2 mt-4">
                <button class="btn btn-ghost flex-1" @click=${() => { this.showDeleteConfirm = false; }}>Cancel</button>
                <button class="btn btn-error flex-1" @click=${this.confirmDeleteGame}>Delete</button>
              </div>
            </div>
          </div>
        ` : nothing}

        <!-- Player selector -->
        <player-selector
          .players=${this.getActivePlayers()}
          .selectedId=${this.selectedPlayerId}
          .pitchCounts=${this.pitches}
          .gameId=${this.currentGame?.id}
          .currentInningPitchers=${this.getCurrentInningPitchers()}
          .viewOnly=${this.viewOnly}
          @player-selected=${this.handlePlayerSelected}
          @player-added=${this.handlePlayerAdded}
        ></player-selector>
        ${this.selectedPlayerId && this.currentInningId ? html`
          <p class="text-xs text-base-content/50 text-center mt-1">Tap another player to switch pitchers mid-inning</p>
        ` : nothing}

        <!-- Pitch counter controls -->
        ${selectedPlayer ? html`
          <div class="card bg-base-100 shadow-lg">
            <div class="card-body items-center text-center py-8">
              ${this.editingJerseyForPlayer === selectedPlayer.id
                ? html`
                    <div class="flex items-center gap-2 mb-1">
                      <span class="text-2xl font-bold text-primary">#</span>
                      <input
                        type="number"
                        inputmode="numeric"
                        min="0" max="999"
                        aria-label="Jersey number"
                        class="input input-bordered input-sm w-20 text-2xl font-bold text-primary tabular-nums text-center"
                        .value=${this.jerseyEditValue}
                        @input=${(e: Event) => { this.jerseyEditValue = (e.target as HTMLInputElement).value; }}
                        @keydown=${(e: KeyboardEvent) => {
                          if (e.key === 'Enter') this.saveJersey(selectedPlayer);
                          else if (e.key === 'Escape') this.cancelEditJersey();
                        }}
                        ?disabled=${this.jerseySaving}
                      />
                      <button type="button" class="btn btn-primary btn-xs" @click=${() => this.saveJersey(selectedPlayer)} ?disabled=${this.jerseySaving}>
                        ${this.jerseySaving ? html`<span class="loading loading-spinner loading-xs"></span>` : 'Save'}
                      </button>
                      <button type="button" class="btn btn-ghost btn-xs" @click=${this.cancelEditJersey} ?disabled=${this.jerseySaving}>Cancel</button>
                    </div>
                  `
                : this.canEditPlayerJersey(selectedPlayer)
                  ? html`
                      <button
                        type="button"
                        class="text-3xl font-bold text-primary tabular-nums hover:underline"
                        aria-label=${selectedPlayer.jersey_number != null ? `Edit jersey number ${selectedPlayer.jersey_number}` : 'Add jersey number'}
                        title="Tap to edit jersey number"
                        @click=${() => this.startEditJersey(selectedPlayer)}
                      >
                        ${selectedPlayer.jersey_number != null ? html`#${selectedPlayer.jersey_number}` : html`<span class="text-base font-normal text-base-content/50">+ jersey #</span>`}
                      </button>
                    `
                  : selectedPlayer.jersey_number != null
                    ? html`<span class="text-3xl font-bold text-primary tabular-nums">#${selectedPlayer.jersey_number}</span>`
                    : nothing}
              <h3 class="text-lg font-semibold mb-2">
                ${selectedPlayer.name}
              </h3>

              <!-- Pitch limit indicator -->
              ${currentPitchStatus
                ? html`
                    <pitch-limit-indicator
                      .status=${currentPitchStatus}
                      class="w-full max-w-xs mb-4"
                    ></pitch-limit-indicator>
                  `
                : nothing}

              <div class="pitch-count ${isAnimating && this.animationType ? this.animationType : ''} my-6">
                ${count}
              </div>

              ${this.viewOnly ? nothing : html`
                <div class="flex gap-6">
                  <button
                    class="btn-pitch btn-error"
                    @click=${this.decrementPitch}
                    ?disabled=${count <= 0 || isLocked}
                  >
                    −
                  </button>
                  <button
                    class="btn-pitch btn-success"
                    @click=${this.incrementPitch}
                    ?disabled=${isLocked}
                  >
                    +
                  </button>
                </div>
              `}

              <p class="text-sm text-base-content/70 mt-4">Pitches</p>
            </div>
          </div>
        ` : html`
          <div class="alert alert-info">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" class="stroke-current shrink-0 w-6 h-6">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <span>Select a player to start counting pitches</span>
          </div>
        `}

        <!-- Quick pitch summary with per-inning breakdown -->
        ${this.players.length > 0 ? html`
          <div class="overflow-x-auto">
            ${this.renderSummaryTable()}
          </div>
        ` : nothing}

        <!-- Complete Game button (shown when pitches recorded) -->
        ${!isLocked && this.getTotalPitchCount() > 0 ? html`
          <div class="pt-4">
            <button
              class="btn btn-primary btn-block gap-2"
              @click=${this.endGame}
            >
              Complete Game
              <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        ` : nothing}
      </div>
    `;
  }

  private renderSummaryTable() {
    const pitchedPlayers = this.players
      .filter((p) => (this.pitches.get(p.id)?.count ?? 0) > 0)
      .sort((a, b) => (this.pitches.get(b.id)?.count ?? 0) - (this.pitches.get(a.id)?.count ?? 0));

    if (pitchedPlayers.length === 0) return nothing;

    // Group by team
    const homeId = this.currentGame?.home_team_id;
    const awayId = this.currentGame?.away_team_id;
    const homeName = this.currentGame?.home_team_name || 'Your Team';
    const awayName = this.currentGame?.away_team_name || 'Opponent';

    const homePlayers = pitchedPlayers.filter((p) => p.team_id && p.team_id === homeId);
    const awayPlayers = pitchedPlayers.filter((p) => p.team_id && p.team_id === awayId);
    const otherPlayers = pitchedPlayers.filter((p) => !p.team_id || (p.team_id !== homeId && p.team_id !== awayId));

    const hasTeams = homeId || awayId;

    const renderHeader = () => html`
      <thead>
        <tr>
          <th class="w-8 text-center">#</th>
          <th>Player</th>
          ${this.inningsData.map((inn) => html`
            <th class="text-center text-xs px-1">${inn.inning_number}${inn.half === 'top' ? '▲' : '▼'}</th>
          `)}
          <th class="text-right">Total</th>
          <th class="text-center">Rest</th>
        </tr>
      </thead>
    `;

    const renderPlayerRow = (player: Player) => {
      const inningCounts = new Map<string, number>();
      for (const inn of this.inningsData) {
        const pip = inn.pitches?.find((p) => p.player_id === player.id);
        if (pip) inningCounts.set(inn.id, pip.pitch_count);
      }
      return html`
        <tr
          class="${player.id === this.selectedPlayerId ? 'bg-primary/10' : ''} hover:bg-base-200 cursor-pointer transition-colors"
          @click=${() => this.handlePlayerSelected(new CustomEvent('player-selected', { detail: player.id }))}
        >
          <td class="text-center font-bold text-primary tabular-nums">${player.jersey_number ?? '--'}</td>
          <td class="font-medium whitespace-nowrap">${player.name}</td>
          ${this.inningsData.map((inn) => html`
            <td class="text-center font-mono text-xs px-1">${inningCounts.get(inn.id) || ''}</td>
          `)}
          <td class="text-right font-mono font-bold">${this.pitches.get(player.id)?.count ?? 0}</td>
          <td class="text-center">
            ${(() => {
              const ps = this.pitchStatus.get(player.id);
              const rest = ps?.rest_status;
              if (!rest || rest.status === 'available') return nothing;
              const dateStr = rest.available_date
                ? new Date(rest.available_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                : '';
              return rest.status === 'resting'
                ? html`<span class="badge badge-error badge-xs">${dateStr}</span>`
                : html`<span class="badge badge-warning badge-xs">${dateStr}</span>`;
            })()}
          </td>
        </tr>
      `;
    };

    const renderSubtotal = (players: Player[], label: string) => {
      const total = players.reduce((sum, p) => sum + (this.pitches.get(p.id)?.count ?? 0), 0);
      if (total === 0) return nothing;
      const colSpan = 2 + this.inningsData.length;
      return html`
        <tr class="bg-base-200/50 font-semibold">
          <td colspan="${colSpan}" class="text-xs text-base-content/70">${label}</td>
          <td class="text-right font-mono">${total}</td>
          <td></td>
        </tr>
      `;
    };

    // If no team grouping, show flat table
    if (!hasTeams) {
      return html`
        <table class="table table-sm">
          ${renderHeader()}
          <tbody>
            ${pitchedPlayers.map(renderPlayerRow)}
          </tbody>
        </table>
      `;
    }

    // Grouped table
    return html`
      <table class="table table-sm">
        ${renderHeader()}
        <tbody>
          ${homePlayers.length > 0 ? html`
            <tr><td colspan="${3 + this.inningsData.length + 1}" class="bg-base-300/50 text-xs font-bold uppercase tracking-wide py-1">${homeName}</td></tr>
            ${homePlayers.map(renderPlayerRow)}
            ${renderSubtotal(homePlayers, `${homeName} Total`)}
          ` : nothing}
          ${awayPlayers.length > 0 ? html`
            <tr><td colspan="${3 + this.inningsData.length + 1}" class="bg-base-300/50 text-xs font-bold uppercase tracking-wide py-1">${awayName}</td></tr>
            ${awayPlayers.map(renderPlayerRow)}
            ${renderSubtotal(awayPlayers, `${awayName} Total`)}
          ` : nothing}
          ${otherPlayers.length > 0 ? html`
            <tr><td colspan="${3 + this.inningsData.length + 1}" class="bg-base-300/50 text-xs font-bold uppercase tracking-wide py-1">Other</td></tr>
            ${otherPlayers.map(renderPlayerRow)}
          ` : nothing}
          ${(homePlayers.length > 0 && awayPlayers.length > 0) || otherPlayers.length > 0 ? html`
            <tr class="font-bold border-t-2">
              <td colspan="${2 + this.inningsData.length}" class="text-xs">Grand Total</td>
              <td class="text-right font-mono">${pitchedPlayers.reduce((s, p) => s + (this.pitches.get(p.id)?.count ?? 0), 0)}</td>
              <td></td>
            </tr>
          ` : nothing}
        </tbody>
      </table>
    `;
  }
}
